import { calendarFromApi, calendarToday } from '@/lib/calendarDate'

export type DocumentPayStatus = 'paid' | 'notPaid' | 'overdue'

type TranslateFn = (key: string, options?: Record<string, unknown>) => string

export function getDocumentPayStatus(input: {
  payStatus?: string | null
  remainingAmount?: number | null
  paidAmount?: number | null
  total?: number | null
  dueDate?: string | Date | null
}): DocumentPayStatus {
  if (isDocumentPaid(input)) return 'paid'

  const dueDay = calendarFromApi(input.dueDate)
  if (dueDay && dueDay < calendarToday()) return 'overdue'

  return 'notPaid'
}

export function formatOverdueDuration(
  dueDate: string | Date | null | undefined,
  t: TranslateFn,
): string {
  const dueDay = calendarFromApi(dueDate)
  const today = calendarToday()
  if (!dueDay || dueDay >= today) return ''

  const [y1, m1, d1] = dueDay.split('-').map(Number)
  const [y2, m2, d2] = today.split('-').map(Number)
  const fromUtc = Date.UTC(y1, m1 - 1, d1)
  const toUtc = Date.UTC(y2, m2 - 1, d2)
  const days = Math.max(1, Math.round((toUtc - fromUtc) / 86_400_000))
  return t(days === 1 ? 'common.durationDay' : 'common.durationDays', { count: days })
}

export function formatOverduePayStatusLabel(
  dueDate: string | Date | null | undefined,
  t: TranslateFn,
  overdueKey: string,
  overdueWithDurationKey: string,
): string {
  const duration = formatOverdueDuration(dueDate, t)
  if (!duration) return t(overdueKey)
  return t(overdueWithDurationKey, { duration })
}

function isDocumentPaid(input: {
  payStatus?: string | null
  remainingAmount?: number | null
  paidAmount?: number | null
  total?: number | null
}): boolean {
  const status = String(input.payStatus ?? '').trim().toLowerCase()
  if (status === 'paid' || status === 'settled') return true

  const remaining = Number(input.remainingAmount)
  const paid = Number(input.paidAmount)
  const total = Number(input.total)

  if (Number.isFinite(remaining) && remaining <= 0) {
    if (Number.isFinite(total) && total > 0) return true
    if (Number.isFinite(paid) && paid > 0) return true
  }

  if (Number.isFinite(paid) && Number.isFinite(total) && total > 0 && paid >= total) return true

  return false
}
